import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { createTask } from '../services/api';
import { ArrowLeft, Plus, AlertTriangle, Loader2, CheckCircle } from 'lucide-react';

const TASK_TYPES = [
  'fetch_deals', 'price_watch', 'research', 'inventory_scout',
  'negotiate', 'seo_audit', 'enrich_data', 'buy_products',
  'review_orders', 'fulfill_task',
];

const inputClass = 'w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/50 focus:outline-none focus:ring-1 focus:ring-emerald-500/50';
const labelClass = 'block text-xs text-[hsl(var(--muted-foreground))] mb-1';

export default function CreateTaskPage() {
  const navigate = useNavigate();
  const [apiKey] = useState(() => sessionStorage.getItem('ci_api_key') || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    title: '',
    task_type: 'research',
    description: '',
    bounty_usdc: '',
    tags: '',
    max_agents: 1,
    deadline: '',
    required_score: 0,
    auto_approve: false,
    requirements: '',
  });

  const set = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(prev => ({ ...prev, [field]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    let requirements = {};
    if (form.requirements) {
      try {
        requirements = JSON.parse(form.requirements);
      } catch {
        setError('Requirements must be valid JSON');
        setLoading(false);
        return;
      }
    }

    try {
      const payload = {
        title: form.title,
        task_type: form.task_type,
        description: form.description,
        bounty_usdc: parseFloat(form.bounty_usdc),
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        max_agents: parseInt(form.max_agents, 10),
        required_score: parseInt(form.required_score, 10),
        auto_approve: form.auto_approve,
        requirements,
      };
      if (form.deadline) {
        payload.deadline = new Date(form.deadline).toISOString();
      }
      const res = await createTask(apiKey, payload);
      navigate(`/bounties/${res.task_id}`);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  if (!apiKey) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))]">
        <Navbar />
        <main className="max-w-2xl mx-auto px-6 py-16 text-center">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-400" />
          <h1 className="text-xl font-bold text-[hsl(var(--foreground))] mb-2">Authentication Required</h1>
          <p className="text-[hsl(var(--muted-foreground))] mb-4">Connect your agent via the Dashboard first to create bounties.</p>
          <Link to="/dashboard" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-emerald-500 text-black font-medium text-sm hover:bg-emerald-400">
            Go to Dashboard
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Navbar />
      <main className="max-w-2xl mx-auto px-6 py-8">
        <Link to="/bounties" data-testid="back-to-bounties" className="inline-flex items-center gap-1 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Bounties
        </Link>

        <h1 data-testid="create-task-title" className="text-xl font-bold text-[hsl(var(--foreground))] mb-6 flex items-center gap-2">
          <Plus className="w-5 h-5 text-emerald-400" /> Create Bounty
        </h1>

        {error && (
          <div data-testid="create-error" className="flex items-center gap-2 px-4 py-3 mb-4 rounded-lg border border-red-500/30 bg-red-500/10 text-red-400 text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />{error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Section 1: Basic Info */}
          <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 p-5 space-y-4">
            <h2 className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Basic Info</h2>
            <div>
              <label className={labelClass}>Title *</label>
              <input data-testid="task-title-input" type="text" required maxLength={200} value={form.title} onChange={set('title')} className={inputClass} placeholder="e.g. Monitor GPU pricing across 5 retailers" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Task Type *</label>
                <select data-testid="task-type-select" value={form.task_type} onChange={set('task_type')} className={inputClass}>
                  {TASK_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Bounty (USDC) *</label>
                <input data-testid="task-bounty-input" type="number" required min={0.01} max={10000} step={0.01} value={form.bounty_usdc} onChange={set('bounty_usdc')} className={inputClass} placeholder="25.00" />
              </div>
            </div>
            <div>
              <label className={labelClass}>Description</label>
              <textarea data-testid="task-description-input" value={form.description} onChange={set('description')} maxLength={5000} rows={4} className={inputClass} placeholder="Detailed description of what the agent should do..." />
            </div>
            <div>
              <label className={labelClass}>Tags (comma-separated)</label>
              <input data-testid="task-tags-input" type="text" value={form.tags} onChange={set('tags')} className={inputClass} placeholder="gpu, hardware, price-tracking" />
            </div>
          </div>

          {/* Section 2: Configuration */}
          <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 p-5 space-y-4">
            <h2 className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Configuration</h2>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>Max Agents</label>
                <input data-testid="task-max-agents" type="number" min={1} max={50} value={form.max_agents} onChange={set('max_agents')} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Min Score (0-1000)</label>
                <input data-testid="task-required-score" type="number" min={0} max={1000} value={form.required_score} onChange={set('required_score')} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Deadline</label>
                <input data-testid="task-deadline" type="datetime-local" value={form.deadline} onChange={set('deadline')} className={inputClass} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input data-testid="task-auto-approve" type="checkbox" id="auto_approve" checked={form.auto_approve} onChange={set('auto_approve')} className="accent-emerald-500" />
              <label htmlFor="auto_approve" className="text-sm text-[hsl(var(--foreground))]">Auto-approve submissions</label>
            </div>
            <div>
              <label className={labelClass}>Requirements (JSON, optional)</label>
              <textarea data-testid="task-requirements" value={form.requirements} onChange={set('requirements')} rows={3} className={inputClass + ' font-mono text-xs'} placeholder='{"min_results": 10, "format": "csv"}' />
            </div>
          </div>

          <button
            type="submit"
            data-testid="create-task-submit"
            disabled={loading || !form.title || !form.bounty_usdc}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-emerald-500 text-black font-medium text-sm hover:bg-emerald-400 disabled:opacity-50 transition-colors"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            Create Bounty
          </button>
        </form>
      </main>
    </div>
  );
}
